// utils/hexToCssFilter.js

export const hexToCssFilter = (hex) => {
    const expandHex = (hextexp) => {
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        return hextexp.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b);
    };

    const hexToRgb = (hexValue) => {
        const expanded = expandHex(hexValue);
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(expanded);
        return result
            ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
            : null;
    };

    class Color {
        constructor(r, g, b) {
            this.set(r, g, b);
        }
        set(r, g, b) {
            this.r = this.clamp(r);
            this.g = this.clamp(g);
            this.b = this.clamp(b);
        }
        clamp(value) {
            return Math.max(0, Math.min(255, value));
        }
        invert(value = 1) {
            this.r = this.clamp((value + (this.r / 255) * (1 - 2 * value)) * 255);
            this.g = this.clamp((value + (this.g / 255) * (1 - 2 * value)) * 255);
            this.b = this.clamp((value + (this.b / 255) * (1 - 2 * value)) * 255);
        }
        sepia(value = 1) {
            this.multiply([
                0.393 + 0.607 * (1 - value), 0.769 - 0.769 * (1 - value), 0.189 - 0.189 * (1 - value),
                0.349 - 0.349 * (1 - value), 0.686 + 0.314 * (1 - value), 0.168 - 0.168 * (1 - value),
                0.272 - 0.272 * (1 - value), 0.534 - 0.534 * (1 - value), 0.131 + 0.869 * (1 - value),
            ]);
        }
        saturate(value = 1) {
            this.multiply([
                0.213 + 0.787 * value, 0.715 - 0.715 * value, 0.072 - 0.072 * value,
                0.213 - 0.213 * value, 0.715 + 0.285 * value, 0.072 - 0.072 * value,
                0.213 - 0.213 * value, 0.715 - 0.715 * value, 0.072 + 0.928 * value,
            ]);
        }
        hueRotate(angle = 0) {
            const rad = (angle / 180) * Math.PI;
            const sin = Math.sin(rad);
            const cos = Math.cos(rad);
            this.multiply([
                0.213 + cos * 0.787 - sin * 0.213, 0.715 - cos * 0.715 - sin * 0.715, 0.072 - cos * 0.072 + sin * 0.928,
                0.213 - cos * 0.213 + sin * 0.143, 0.715 + cos * 0.285 + sin * 0.14, 0.072 - cos * 0.072 - sin * 0.283,
                0.213 - cos * 0.213 - sin * 0.787, 0.715 - cos * 0.715 + sin * 0.715, 0.072 + cos * 0.928 + sin * 0.072,
            ]);
        }
        brightness(value = 1) { this.linear(value); }
        contrast(value = 1) { this.linear(value, -(0.5 * value) + 0.5); }
        linear(slope = 1, intercept = 0) {
            this.r = this.clamp(this.r * slope + intercept * 255);
            this.g = this.clamp(this.g * slope + intercept * 255);
            this.b = this.clamp(this.b * slope + intercept * 255);
        }
        multiply(matrix) {
            const newR = this.clamp(this.r * matrix[0] + this.g * matrix[1] + this.b * matrix[2]);
            const newG = this.clamp(this.r * matrix[3] + this.g * matrix[4] + this.b * matrix[5]);
            const newB = this.clamp(this.r * matrix[6] + this.g * matrix[7] + this.b * matrix[8]);
            this.set(newR, newG, newB);
        }
    }

    class Solver {
        constructor(target) {
            this.target = target;
            this.reusedColor = new Color(0, 0, 0);
        }

        solve() {
            const result = this.solveNarrow(this.solveWide());
            return this.raw(result.values);
        }

        solveWide() {
            const A = 5, c = 15;
            const a = [60, 180, 18000, 600, 1.2, 1.2];
            let best = { loss: Infinity };
            for (let i = 0; best.loss > 25 && i < 3; i++) {
                const initial = [50, 20, 3750, 50, 100, 100];
                const result = this.spsa(A, a, c, initial, 1000);
                if (result.loss < best.loss) best = result;
            }
            return best;
        }

        solveNarrow(wide) {
            const A = wide.loss, c = 2;
            const A1 = A + 1;
            const a = [0.25 * A1, 0.25 * A1, A1, 0.25 * A1, 0.2 * A1, 0.2 * A1];
            return this.spsa(A, a, c, wide.values, 500);
        }

        spsa(A, a, c, values, iters) {
            const alpha = 1, gamma = 1 / 6;
            let best = null, bestLoss = Infinity;
            const deltas = new Array(6), highArgs = new Array(6), lowArgs = new Array(6);

            for (let k = 0; k < iters; k++) {
                const ck = c / Math.pow(k + 1, gamma);
                for (let i = 0; i < 6; i++) {
                    deltas[i] = Math.random() > 0.5 ? 1 : -1;
                    highArgs[i] = values[i] + ck * deltas[i];
                    lowArgs[i] = values[i] - ck * deltas[i];
                }

                const lossDiff = this.loss(highArgs) - this.loss(lowArgs);
                for (let i = 0; i < 6; i++) {
                    const g = (lossDiff / (2 * ck)) * deltas[i];
                    const ak = a[i] / Math.pow(A + k + 1, alpha);
                    values[i] = this.fix(values[i] - ak * g, i);
                }

                const loss = this.loss(values);
                if (loss < bestLoss) {
                    best = values.slice();
                    bestLoss = loss;
                }
            }
            return { values: best, loss: bestLoss };
        }

        fix(value, idx) {
            let max = 100;
            if (idx === 2) max = 7500;
            else if (idx === 4 || idx === 5) max = 200;

            if (idx === 3) {
                if (value > max) value %= max;
                else if (value < 0) value = max + (value % max);
            } else if (value < 0) value = 0;
            else if (value > max) value = max;

            return value;
        }

        loss(filters) {
            const c = this.reusedColor;
            c.set(0, 0, 0);
            c.invert(filters[0] / 100);
            c.sepia(filters[1] / 100);
            c.saturate(filters[2] / 100);
            c.hueRotate(filters[3] * 3.6);
            c.brightness(filters[4] / 100);
            c.contrast(filters[5] / 100);
            return Math.abs(c.r - this.target.r) + Math.abs(c.g - this.target.g) + Math.abs(c.b - this.target.b);
        }

        raw(filters) {
            const fmt = (i, mul = 1) => Math.round(filters[i] * mul);
            // ✅ Fixed: close the template literal properly
            return `brightness(0) saturate(100%) invert(${fmt(0)}%) sepia(${fmt(1)}%) saturate(${fmt(2)}%) hue-rotate(${fmt(3, 3.6)}deg) brightness(${fmt(4)}%) contrast(${fmt(5)}%)`;
        }
    }

    const rgb = hexToRgb(hex);
    if (!rgb) return "none";

    const color = new Color(rgb[0], rgb[1], rgb[2]);
    const solver = new Solver(color);
    return solver.solve();
};
